import { useEffect, useState, useMemo } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  User,
  Shield,
  UserCheck,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import {
  authService,
  pontoService,
  usuarioService,
} from "../../services/apiService";
import HorarioTrabalhoCampos, {
  JORNADA_VAZIA,
  formularioParaJornada,
  jornadaParaFormulario,
} from "../GestaoPonto/HorarioTrabalhoCampos";
import { hojeIso } from "../GestaoPonto/pontoUtils";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import PageContainer from "../../components/PageContainer";
import { ToggleSwitch } from "../../components/ui";

const Usuario = () => {
  const usuarioLogado = useMemo(() => authService.getCurrentUser(), []);
  const sessaoOrganizacao = useMemo(() => authService.getSessaoOrganizacao(), []);
  const podeAcessarPagina = authService.hasRole(
    usuarioLogado,
    "admin",
    sessaoOrganizacao
  );

  const [usuarios, setUsuarios] = useState([]);
  const [organizacoesPermitidas, setOrganizacoesPermitidas] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [loadingOrganizacoes, setLoadingOrganizacoes] = useState(false);
  const [acessoBloqueado, setAcessoBloqueado] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [paginacao, setPaginacao] = useState({
    paginaAtual: 1,
    itensPorPagina: 6,
  });
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    password: "",
    confirmPassword: "",
    permissaoOrganizacao: "USER",
    organizacaoIds: [],
    ativo: true,
  });
  // Ponto: cargo e horário de trabalho (salvos no módulo de ponto junto com o usuário).
  const [jornadaForm, setJornadaForm] = useState(JORNADA_VAZIA);
  const [tinhaJornada, setTinhaJornada] = useState(false);
  const { showError, showSuccess } = useNotification();
  const confirmDialog = useConfirmDialog();

  const organizacoesDisponiveis = useMemo(() => {
    return Array.isArray(organizacoesPermitidas)
      ? organizacoesPermitidas.filter((organizacao) => organizacao?.ativo !== false)
      : [];
  }, [organizacoesPermitidas]);

  const loadUsuarios = async (nome = "") => {
    setLoadingUsuarios(true);
    try {
      const data = await usuarioService.listarUsuarios(nome);
      setUsuarios(Array.isArray(data) ? data : []);
      setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
    } catch (err) {
      showError(err.message || "Erro ao carregar usuários");
      setUsuarios([]);
    } finally {
      setLoadingUsuarios(false);
    }
  };

  const loadOrganizacoesPermitidas = async () => {
    setLoadingOrganizacoes(true);
    try {
      const data = sessaoOrganizacao?.organizacaoId
        ? await usuarioService.listarOrganizacoesPermitidasNoContexto(
            sessaoOrganizacao.organizacaoId
          )
        : await usuarioService.listarOrganizacoesPermitidas();

      setOrganizacoesPermitidas(Array.isArray(data) ? data : []);
      setAcessoBloqueado(false);
      return true;
    } catch (err) {
      setOrganizacoesPermitidas([]);
      if (err?.status === 403) {
        setAcessoBloqueado(true);
        showError(
          err.message ||
            "Você não tem acesso ao contexto de organização atual."
        );
      } else {
        showError(err.message || "Erro ao carregar organizações permitidas");
      }
      return false;
    } finally {
      setLoadingOrganizacoes(false);
    }
  };

  useEffect(() => {
    if (!podeAcessarPagina) {
      return;
    }

    const carregarDados = async () => {
      const podeCarregarUsuarios = await loadOrganizacoesPermitidas();

      if (podeCarregarUsuarios) {
        await loadUsuarios();
      }
    };

    carregarDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeAcessarPagina]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    const timeoutId = setTimeout(() => {
      loadUsuarios(value.trim());
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const totalRegistros = usuarios.length;
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / paginacao.itensPorPagina));
  const indiceInicial = (paginacao.paginaAtual - 1) * paginacao.itensPorPagina;
  const indiceFinal = indiceInicial + paginacao.itensPorPagina;
  const usuariosPaginados = usuarios.slice(indiceInicial, indiceFinal);

  const irParaPrimeiraPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: totalPaginas }));
  };

  const irParaPaginaAnterior = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPagina = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginas, prev.paginaAtual + 1),
    }));
  };

  const irParaPagina = (numeroPagina) => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: numeroPagina }));
  };

  const gerarNumerosPaginas = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacao.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginas, inicio + maxPaginasVisiveis - 1);

    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i += 1) {
      paginas.push(i);
    }

    return paginas;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleOrganizacao = (organizacaoId) => {
    setFormData((prev) => {
      const organizacaoIds = Array.isArray(prev.organizacaoIds)
        ? prev.organizacaoIds
        : [];

      if (organizacaoIds.includes(organizacaoId)) {
        return {
          ...prev,
          organizacaoIds: organizacaoIds.filter((id) => id !== organizacaoId),
        };
      }

      return {
        ...prev,
        organizacaoIds: [...organizacaoIds, organizacaoId],
      };
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    if (!formData.nome.trim()) {
      showError("Nome é obrigatório");
      return;
    }

    if (!formData.email.trim()) {
      showError("Email é obrigatório");
      return;
    }

    if (!validateEmail(formData.email)) {
      showError("Email deve ter um formato válido");
      return;
    }

    if (!Array.isArray(formData.organizacaoIds) || formData.organizacaoIds.length === 0) {
      showError("Selecione pelo menos uma organização");
      return;
    }

    if (!editingItem) {
      if (!formData.password.trim()) {
        showError("Senha é obrigatória");
        return;
      }

      if (formData.password.length < 6) {
        showError("Senha deve ter pelo menos 6 caracteres");
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        showError("Senhas não coincidem");
        return;
      }
    }

    if (jornadaForm.participa) {
      if (!jornadaForm.dataInicio) {
        showError("Informe o início do controle de ponto");
        return;
      }
      if (!jornadaForm.semanaEntrada1 && !jornadaForm.sabadoEntrada1) {
        showError("Informe o horário de trabalho (segunda a sexta ou sábado)");
        return;
      }
    }

    setSaving(true);

    try {
      const dataToSend = {
        nome: formData.nome,
        email: formData.email,
        permissao: editingItem ? editingItem.permissao || "USER" : "USER",
        permissaoOrganizacao: formData.permissaoOrganizacao,
        organizacaoIds: formData.organizacaoIds,
        organizacaoId: formData.organizacaoIds[0] || "",
        ativo: formData.ativo,
      };

      if (!editingItem) {
        dataToSend.password = formData.password;
      }

      let usuarioId = editingItem?.id;
      if (editingItem) {
        await usuarioService.atualizarUsuario(editingItem.id, dataToSend);
      } else {
        const criado = await usuarioService.cadastrarUsuario(dataToSend);
        usuarioId = criado?.id;
      }

      if (usuarioId && (jornadaForm.participa || tinhaJornada)) {
        try {
          await pontoService.salvarJornada(
            usuarioId,
            formularioParaJornada(jornadaForm),
          );
        } catch (erroPonto) {
          showError(
            `Usuário salvo, mas o horário de trabalho não: ${erroPonto.message}`,
          );
          await loadUsuarios(searchTerm);
          return;
        }
      }

      showSuccess(
        editingItem
          ? "Usuário atualizado com sucesso!"
          : "Usuário criado com sucesso!",
      );

      await loadUsuarios(searchTerm);
      handleCancel();
    } catch (err) {
      showError(err.message || "Erro ao salvar usuário");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (item) => {
    setSaving(true);
    try {
      let usuarioParaEditar = item;

      if (
        !item?.organizacaoId &&
        !item?.organizacao?.id &&
        !Array.isArray(item?.organizacoes) &&
        !Array.isArray(item?.organizacaoIds)
      ) {
        const usuarioCompleto = await usuarioService.listarUsuarioPorId(item.id);
        usuarioParaEditar = usuarioCompleto || item;
      }

      const organizacaoIds = Array.isArray(usuarioParaEditar?.organizacaoIds)
        ? usuarioParaEditar.organizacaoIds
        : Array.isArray(usuarioParaEditar?.organizacoes)
        ? usuarioParaEditar.organizacoes
            .map((organizacao) => organizacao?.id || organizacao?.organizacaoId)
            .filter(Boolean)
        : usuarioParaEditar?.organizacaoId || usuarioParaEditar?.organizacao?.id
        ? [usuarioParaEditar.organizacaoId || usuarioParaEditar.organizacao?.id]
        : [];

      const colaboradoras = await pontoService
        .listarColaboradoras()
        .catch(() => []);
      const jornada = colaboradoras.find(
        (colaboradora) => colaboradora.usuarioId === usuarioParaEditar.id,
      )?.jornada;
      setTinhaJornada(Boolean(jornada));
      setJornadaForm(jornadaParaFormulario(jornada));

      setEditingItem(usuarioParaEditar);
      setFormData({
        nome: usuarioParaEditar.nome || "",
        email: usuarioParaEditar.email || "",
        password: "",
        confirmPassword: "",
        permissaoOrganizacao:
          usuarioParaEditar.permissaoOrganizacao ||
          usuarioParaEditar?.organizacoes?.[0]?.permissao ||
          usuarioParaEditar.permissao ||
          "USER",
        organizacaoIds,
        ativo:
          usuarioParaEditar.ativo !== undefined ? usuarioParaEditar.ativo : true,
      });
      setShowForm(true);
    } catch (err) {
      showError(err.message || "Erro ao carregar dados do usuário");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir este usuário?",
    });

    if (!confirmed) {
      return;
    }

    setSaving(true);

    try {
      await usuarioService.deletarUsuario(id);
      showSuccess("Usuário excluído com sucesso!");
      await loadUsuarios(searchTerm);
    } catch (err) {
      showError(err.message || "Erro ao excluir usuário");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingItem(null);
    setTinhaJornada(false);
    setJornadaForm(JORNADA_VAZIA);
    setFormData({
      nome: "",
      email: "",
      password: "",
      confirmPassword: "",
      permissaoOrganizacao: "USER",
      organizacaoIds: [],
      ativo: true,
    });
  };

  const handleNew = () => {
    if (!podeAcessarPagina) {
      showError("Você não tem permissão para acessar este fluxo");
      return;
    }

    if (acessoBloqueado) {
      showError("Você não tem acesso ao contexto de organização atual.");
      return;
    }

    if (!loadingOrganizacoes && organizacoesDisponiveis.length === 0) {
      showError("Nenhuma organização disponível para cadastro");
      return;
    }

    setEditingItem(null);
    setTinhaJornada(false);
    setJornadaForm({ ...JORNADA_VAZIA, dataInicio: hojeIso() });
    setFormData({
      nome: "",
      email: "",
      password: "",
      confirmPassword: "",
      permissaoOrganizacao: "USER",
      organizacaoIds:
        organizacoesDisponiveis.length === 1
          ? [organizacoesDisponiveis[0].id]
          : [],
      ativo: true,
    });
    setShowForm(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const getPermissaoLabel = (permissao) => {
    return permissao === "ADMIN" ? "Administrador" : "Usuário";
  };

  const getPermissaoIcon = (permissao) => {
    return permissao === "ADMIN" ? Shield : UserCheck;
  };

  if (!podeAcessarPagina) {
    return (
      <PageContainer className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-800">
          <h1 className="text-2xl font-bold mb-2">Usuários</h1>
          <p>Você não tem permissão para acessar este fluxo.</p>
        </div>
      </PageContainer>
    );
  }

  if (acessoBloqueado) {
    return (
      <PageContainer className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-800">
          <h1 className="text-2xl font-bold mb-2">Usuários</h1>
          <p>Você não tem acesso ao contexto de organização atual.</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Usuários</h1>
        <p className="text-gray-600">
          Gerencie os usuários e suas permissões no sistema
        </p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Pesquisar por nome..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <button
          onClick={handleNew}
          disabled={saving || loadingUsuarios || loadingOrganizacoes || organizacoesDisponiveis.length === 0}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={20} />
          Novo Usuário
        </button>
      </div>

      {!loadingOrganizacoes && organizacoesDisponiveis.length === 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800">
          Nenhuma organização disponível para cadastrar usuário.
        </div>
      )}

      {showForm && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">
            {editingItem ? "Editar Usuário" : "Novo Usuário"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label
                htmlFor="nome"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Nome Completo *
              </label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Digite o nome completo"
                maxLength={100}
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="usuario@email.com"
              />
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Organizações *
              </label>
              <span className="text-xs text-gray-500">
                {Array.isArray(formData.organizacaoIds)
                  ? `${formData.organizacaoIds.length} selecionada(s)`
                  : "0 selecionada(s)"}
              </span>
            </div>
            <div className="border border-gray-300 rounded-md bg-white p-4">
              {loadingOrganizacoes ? (
                <p className="text-xs text-gray-500">Carregando organizações...</p>
              ) : organizacoesDisponiveis.length === 0 ? (
                <p className="text-xs text-gray-500">Nenhuma organização disponível.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {organizacoesDisponiveis.map((organizacao) => {
                    const checked = Array.isArray(formData.organizacaoIds)
                      ? formData.organizacaoIds.includes(organizacao.id)
                      : false;

                    return (
                      <label
                        key={organizacao.id}
                        className="flex items-center gap-3 rounded-md border border-gray-200 px-3 py-3 cursor-pointer hover:bg-gray-50 min-h-12"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOrganizacao(organizacao.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="flex-1">
                          <span className="block text-sm font-medium text-gray-900">
                            {organizacao.nome}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {!editingItem && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Senha *
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Digite a senha"
                  minLength={6}
                />
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Confirmar Senha *
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Confirme a senha"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label
                htmlFor="permissaoOrganizacao"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Permissão *
              </label>
              <select
                id="permissaoOrganizacao"
                name="permissaoOrganizacao"
                value={formData.permissaoOrganizacao}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="USER">Usuário</option>
                <option value="ADMIN">Administrador</option>
              </select>
            </div>

            <div className="flex items-center md:pt-8">
              <ToggleSwitch
                name="ativo"
                checked={formData.ativo}
                onChange={handleChange}
                label="Usuário ativo"
                aria-label="Usuário ativo"
              />
            </div>
          </div>

          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h4 className="mb-3 font-semibold text-gray-900">
              Ponto e horário de trabalho
            </h4>
            <HorarioTrabalhoCampos
              formulario={jornadaForm}
              onChange={setJornadaForm}
              prefixoId="usuario-jornada"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={
                saving ||
                !formData.nome.trim() ||
                !formData.email.trim() ||
                !Array.isArray(formData.organizacaoIds) ||
                formData.organizacaoIds.length === 0
              }
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Save size={16} />
              {saving ? "Salvando..." : "Salvar"}
            </button>

            <button
              onClick={handleCancel}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <X size={16} />
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow border">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Lista de Usuários</h3>
          <span className="text-sm text-gray-500">
            {totalRegistros} registro(s) encontrado(s)
          </span>
        </div>

        {loadingUsuarios && usuarios.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Carregando usuários...
          </div>
        ) : usuarios.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
              {searchTerm ? "Nenhum usuário encontrado" : "Nenhum usuário cadastrado"}
          </div>
        ) : (
          <>
            <div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Permissão
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {usuariosPaginados.map((item) => {
                    const permissaoVinculo =
                      item.permissaoOrganizacao ||
                      item?.organizacoes?.[0]?.permissao ||
                      item.permissao ||
                      "USER";
                    const PermissaoIcon = getPermissaoIcon(permissaoVinculo);
                    return (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8">
                              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <User className="h-4 w-4 text-blue-600" />
                              </div>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {item.nome}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{item.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <PermissaoIcon
                              className={`h-4 w-4 mr-2 ${
                                permissaoVinculo === "ADMIN"
                                  ? "text-purple-600"
                                  : "text-blue-600"
                              }`}
                            />
                            <span
                              className={`text-sm font-medium ${
                                permissaoVinculo === "ADMIN"
                                  ? "text-purple-700"
                                  : "text-blue-700"
                              }`}
                            >
                              {getPermissaoLabel(permissaoVinculo)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              item.ativo
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {item.ativo ? "Ativo" : "Inativo"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEdit(item)}
                              disabled={saving}
                              className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 p-1 rounded transition-colors"
                              title="Editar"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              disabled={saving}
                              className="text-red-600 hover:text-red-900 disabled:text-gray-400 p-1 rounded transition-colors"
                              title="Excluir"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPaginas > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Mostrando {indiceInicial + 1} até {Math.min(indiceFinal, totalRegistros)} de {totalRegistros} registros
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={irParaPrimeiraPagina}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Primeira página"
                    >
                      <ChevronsLeft size={16} />
                    </button>

                    <button
                      onClick={irParaPaginaAnterior}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Página anterior"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    <div className="flex space-x-1">
                      {gerarNumerosPaginas().map((numeroPagina) => (
                        <button
                          key={numeroPagina}
                          onClick={() => irParaPagina(numeroPagina)}
                          className={`px-3 py-2 rounded-md text-sm font-medium ${
                            paginacao.paginaAtual === numeroPagina
                              ? "bg-blue-600 text-white"
                              : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {numeroPagina}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={irParaProximaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Próxima página"
                    >
                      <ChevronRight size={16} />
                    </button>

                    <button
                      onClick={irParaUltimaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Última página"
                    >
                      <ChevronsRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default Usuario;
